import mergedRoutes from 'constants/router'
import { matchPath } from 'react-router'
import { domainManifests } from 'store/domains'
import { ROUTE_ACCESS } from 'utils/routeAccess'

const routePathCandidates = (path) => {
  if (!path) {
    return []
  }

  return Array.isArray(path) ? path.filter(Boolean) : [path]
}

const collectPathDomainEntries = (items, inheritedDomain, acc) => {
  if (!items || !Array.isArray(items)) {
    return
  }

  for (const route of items) {
    const domain = route.domain ?? inheritedDomain

    if (domain && route.path) {
      for (const p of routePathCandidates(route.path)) {
        acc.push({
          path: p,
          exact: route.exact === true,
          domain,
        })
      }
    }

    if (route.routes) {
      const nextInherited = route.domain ?? inheritedDomain
      collectPathDomainEntries(route.routes, nextInherited, acc)
    }
  }
}

const pathDomainEntries = []
collectPathDomainEntries(mergedRoutes, undefined, pathDomainEntries)

const buildRouteKeyDomainIndex = (routes) => {
  const index = {}

  const walk = (items, inheritedDomain) => {
    if (!items || !Array.isArray(items)) {
      return
    }

    for (const route of items) {
      const effectiveDomain = route.domain ?? inheritedDomain

      if (route.key && effectiveDomain) {
        index[route.key] = effectiveDomain
      }

      const nextInherited = route.domain ?? inheritedDomain

      if (route.routes) {
        walk(route.routes, nextInherited)
      }
    }
  }

  walk(routes, undefined)

  return index
}

const routeKeyDomainIndex = buildRouteKeyDomainIndex(mergedRoutes)

const pathWithoutI18nParam = (path) => path.replace(/^\/:i18n\b/, '') || '/'

/**
 * Manual preload entry per domain (merged over router-derived defaults).
 * @type {Record<string, { routeKey?: string, routePath?: string }>}
 *
 * @example
 * recommendations: {
 *   routeKey: 'recommendations/audits',
 *   routePath: '/recommendations/audits',
 * },
 */
export const PRELOAD_DOMAIN_OVERRIDES = {}

/** Prefer top-level nav routes (exact, visible, short path) as preload entry per domain. */
const scorePreloadRoute = (route, path) => {
  let score = 0

  if (route.exact === true) {
    score += 1000
  }

  if (!route.hidden) {
    score += 200
  }

  if (route.icon) {
    score += 100
  }

  score -= pathWithoutI18nParam(path).split('/').filter(Boolean).length * 5

  return score
}

const applyPreloadDomainOverrides = (bestByDomain, overrides, manifestDomains) => {
  for (const [domain, override] of Object.entries(overrides)) {
    if (!override || !manifestDomains.has(domain)) {
      continue
    }

    const current = bestByDomain.get(domain)
    const routeKey = override.routeKey ?? current?.routeKey
    const routePath = override.routePath ?? current?.routePath

    if (!routeKey || !routePath) {
      continue
    }

    bestByDomain.set(domain, {
      routeKey,
      routePath,
      score: current?.score ?? Number.POSITIVE_INFINITY,
    })
  }
}

/**
 * One representative route per domain for background preload.
 * Domains come from manifests (product-specific); paths/keys from merged router.
 * @param {string | null} [accessFilter] — only routes with this `access` (inherited from parents).
 */
export const buildPreloadDomainsFromRoutes = (
  routes,
  manifestDomainKeys,
  overrides = PRELOAD_DOMAIN_OVERRIDES,
  accessFilter = null,
) => {
  const manifestDomains = new Set(manifestDomainKeys)
  const bestByDomain = new Map()

  const walk = (items, inheritedDomain, inheritedAccess) => {
    if (!items || !Array.isArray(items)) {
      return
    }

    for (const route of items) {
      const domain = route.domain ?? inheritedDomain
      const access = route.access ?? inheritedAccess

      if (
        domain &&
        route.key &&
        route.path &&
        manifestDomains.has(domain) &&
        (!accessFilter || access === accessFilter)
      ) {
        for (const path of routePathCandidates(route.path)) {
          const score = scorePreloadRoute(route, path)
          const prev = bestByDomain.get(domain)

          if (!prev || score > prev.score) {
            bestByDomain.set(domain, {
              routeKey: route.key,
              routePath: pathWithoutI18nParam(path),
              score,
            })
          }
        }
      }

      if (route.routes) {
        walk(route.routes, route.domain ?? inheritedDomain, access)
      }
    }
  }

  walk(routes, undefined, undefined)
  applyPreloadDomainOverrides(bestByDomain, overrides, manifestDomains)

  return Array.from(bestByDomain.values())
    .map(({ routeKey, routePath }) => ({ routeKey, routePath }))
    .sort((a, b) => a.routePath.localeCompare(b.routePath))
}

const manifestDomainKeys = Object.keys(domainManifests)

export const PRELOAD_DOMAINS_PRIVATE = buildPreloadDomainsFromRoutes(
  mergedRoutes,
  manifestDomainKeys,
  PRELOAD_DOMAIN_OVERRIDES,
  ROUTE_ACCESS.PRIVATE,
)

export const PRELOAD_DOMAINS_GUEST = buildPreloadDomainsFromRoutes(
  mergedRoutes,
  manifestDomainKeys,
  PRELOAD_DOMAIN_OVERRIDES,
  ROUTE_ACCESS.GUEST,
)

/**
 * Guest routes for background preload (domain + page chunk per route).
 */
export const buildPreloadRoutesFromRoutes = (routes, accessFilter) => {
  const byKey = new Map()

  const walk = (items, inheritedAccess) => {
    if (!items || !Array.isArray(items)) {
      return
    }

    for (const route of items) {
      const access = route.access ?? inheritedAccess

      if (
        route.key &&
        route.path &&
        route.component &&
        (!accessFilter || access === accessFilter) &&
        !byKey.has(route.key)
      ) {
        for (const path of routePathCandidates(route.path)) {
          byKey.set(route.key, {
            routeKey: route.key,
            routePath: pathWithoutI18nParam(path),
            component: route.component,
          })
          break
        }
      }

      if (route.routes) {
        walk(route.routes, access)
      }
    }
  }

  walk(routes, undefined)

  return Array.from(byKey.values()).sort((a, b) => a.routePath.localeCompare(b.routePath))
}

export const PRELOAD_GUEST_ROUTES = buildPreloadRoutesFromRoutes(mergedRoutes, ROUTE_ACCESS.GUEST)

/** @deprecated Use PRELOAD_DOMAINS_PRIVATE */
export const PRELOAD_DOMAINS = PRELOAD_DOMAINS_PRIVATE

export const resolveDomainsForPathname = (pathname) => {
  if (!pathname) {
    return []
  }

  let bestDomain = null
  let bestScore = -1

  for (const { path, exact, domain } of pathDomainEntries) {
    const m = matchPath(pathname, { path, exact, strict: false })
    if (!m || !domain) {
      continue
    }

    const score = path.length
    if (score > bestScore) {
      bestScore = score
      bestDomain = domain
    }
  }

  return bestDomain ? [bestDomain] : []
}

export const resolveDomainsForRouteKey = (routeKey) => {
  if (!routeKey) {
    return []
  }

  const fromRoutes = routeKeyDomainIndex[routeKey]
  if (fromRoutes) {
    return [fromRoutes]
  }

  return []
}

/** Pathname first, then route key when pathname is missing or unmatched. */
export const resolveDomainsForRoute = (pathname, routeKey) => {
  if (pathname) {
    const fromPathname = resolveDomainsForPathname(pathname)
    if (fromPathname.length) {
      return fromPathname
    }
  }
  return resolveDomainsForRouteKey(routeKey)
}
